import React, { useCallback, useEffect, useState, useMemo } from "react";
import AllInterviewLogsStats from "./AllInterviewLogsStats";
import AllInterviewLogsHeader from "./AllInterviewLogsHeader";
import InterviewLogFilter from "./InterviewLogFilter";
import InterviewLogTable from "./InterviewLogTable";
import {
  RootStore,
  useAppDispatch,
  useAppSelector,
} from "../../../store/store";
import { fetchInterviewLogs } from "../../../store/features/AdminPanel/InterviewLogs/InterviewLogsApi";
import "./allInterviewLogsContent.scss";

const AllInterviewLogsContent = () => {
  const authState = useAppSelector((store: RootStore) => store.AuthSlice);
  const interviewLogsState = useAppSelector(
    (store: RootStore) => store.InterviewLogsSlice
  );
  const dispatch = useAppDispatch();

  const [currentPage, setCurrentPage] = useState(1);
  const [allInterviewLogs, setAllInterviewLogs] = useState<any[]>([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInterviewUser, setSelectedInterviewUser] = useState<
    string | undefined
  >("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);

  // Fetch all data once
  useEffect(() => {
    if (!authState?.tac_user?.id && !authState?.auth_user?.id) {
      return;
    }

    const subdomain = authState?.tenant || authState?.auth_user?.subdomain;
    const queryParams: any = {
      page: 1,
      limit: 20, // Fetch all data
    };

    // Add subdomain to queryParams if it's "theagileacademy" or "aa"
    if (subdomain === "theagileacademy" || subdomain === "aa") {
      queryParams.subdomain = subdomain;
    }

    dispatch(fetchInterviewLogs(queryParams));
  }, [
    authState?.tac_user?.id,
    authState?.auth_user?.id,
    authState?.tenant,
    authState?.auth_user?.subdomain,
    dispatch,
  ]);

  // Update allInterviewLogs when data is fetched
  useEffect(() => {
    if (interviewLogsState.interviewLogs.length > 0) {
      setAllInterviewLogs(interviewLogsState.interviewLogs);
    }
  }, [interviewLogsState.interviewLogs]);

  // Apply client-side filtering
  const filteredInterviewLogs = useMemo(() => {
    let filtered = [...allInterviewLogs];

    // Apply search filter
    if (searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((interview) => {
        const userName = interview.user?.name?.toLowerCase() || "";
        const userEmail = interview.user?.email?.toLowerCase() || "";
        const interviewType = interview.interviewType?.toLowerCase() || "";
        const role = interview.role?.toLowerCase() || "";

        return (
          userName.includes(searchLower) ||
          userEmail.includes(searchLower) ||
          interviewType.includes(searchLower) ||
          role.includes(searchLower)
        );
      });
    }

    // Apply user filter
    if (selectedInterviewUser && selectedInterviewUser !== "all") {
      filtered = filtered.filter((interview) => {
        const userId = interview.userId || interview.user?.id;
        return userId === selectedInterviewUser;
      });
    }

    // Apply date filters
    if (dateFrom) {
      filtered = filtered.filter((interview) => {
        const interviewDate = new Date(interview.date);
        return interviewDate >= dateFrom;
      });
    }

    if (dateTo) {
      filtered = filtered.filter((interview) => {
        const interviewDate = new Date(interview.date);
        // Set time to end of day for dateTo comparison
        const endOfDay = new Date(dateTo);
        endOfDay.setHours(23, 59, 59, 999);
        return interviewDate <= endOfDay;
      });
    }

    return filtered;
  }, [allInterviewLogs, searchTerm, selectedInterviewUser, dateFrom, dateTo]);

  // Calculate pagination for filtered results
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredInterviewLogs.length / itemsPerPage);
  const paginatedInterviewLogs = filteredInterviewLogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const clearAllFilters = () => {
    setSearchTerm("");
    setSelectedInterviewUser("all");
    setDateFrom(undefined);
    setDateTo(undefined);
    setCurrentPage(1);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedInterviewUser, dateFrom, dateTo]);

  return (
    <div className="all-interview-logs-content">
      <AllInterviewLogsHeader />
      <AllInterviewLogsStats />
      <InterviewLogFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        selectedInterviewUser={selectedInterviewUser}
        setSelectedInterviewUser={setSelectedInterviewUser}
        dateFrom={dateFrom}
        setDateFrom={setDateFrom}
        dateTo={dateTo}
        setDateTo={setDateTo}
        clearAllFilters={clearAllFilters}
        allInterviewLogs={allInterviewLogs}
      />
      <InterviewLogTable
        handlePageChange={handlePageChange}
        currentPage={currentPage}
        interviewLogs={paginatedInterviewLogs}
        total={filteredInterviewLogs.length}
        totalPages={totalPages}
        loading={interviewLogsState.loading.fetchInterviewLogs}
      />
    </div>
  );
};

export default AllInterviewLogsContent;
